import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Snackbar, Alert, Slide, Box } from '@mui/material';
import { color, radius, shadow } from '../../theme/tokens';

// Lightweight toast system — replaces per-page inline success/error banners.
const FeedbackContext = createContext(null);

let seq = 0;

export const FeedbackProvider = ({ children }) => {
  const [toast, setToast] = useState(null);

  const notify = useCallback((message, severity = 'success', options = {}) => {
    if (!message) return;
    setToast({ id: ++seq, message, severity, ...options });
  }, []);

  const api = useMemo(() => ({
    notify,
    success: (msg, opts) => notify(msg, 'success', opts),
    error:   (msg, opts) => notify(msg, 'error', opts),
    info:    (msg, opts) => notify(msg, 'info', opts),
    warning: (msg, opts) => notify(msg, 'warning', opts),
    dismiss: () => setToast(null),
  }), [notify]);

  return (
    <FeedbackContext.Provider value={api}>
      {children}
      <Snackbar
        key={toast?.id}
        open={!!toast}
        autoHideDuration={toast?.duration ?? 3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={(props) => <Slide {...props} direction="up" />}
        sx={{ bottom: { xs: 16, sm: 24 } }}
      >
        {toast ? (
          <Box
            sx={{
              bgcolor: color.surface,
              border: `1px solid ${color.border}`,
              boxShadow: shadow.lg,
              borderRadius: `${radius.lg}px`,
              px: 2, py: 1.25,
              minWidth: 280,
            }}
          >
            <Alert
              severity={toast.severity}
              variant="standard"
              onClose={() => setToast(null)}
              sx={{ p: 0, bgcolor: 'transparent', border: 'none', color: 'inherit', '& .MuiAlert-icon': { mr: 1 } }}
            >
              {toast.message}
            </Alert>
          </Box>
        ) : <span />}
      </Snackbar>
    </FeedbackContext.Provider>
  );
};

export const useFeedback = () => {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('useFeedback must be used within FeedbackProvider');
  return ctx;
};

export default FeedbackProvider;
